import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PlayCircleFilledRounded from "@mui/icons-material/PlayCircleFilledRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import PublicRounded from "@mui/icons-material/PublicRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import type { StreamData } from "../../../types/types";
import { CalendarIcon, LocationIcon } from "../../../../../../components/icons";

interface StreamSectionProps {
  streamTabs: string[];
  stream: StreamData;

  activeTab: number;
  onTabChange: (newIndex: number) => void;

  onViewStream?: () => void;
  onOpenMemorialPage?: () => void;
  onOpenCallsheet?: () => void;
}

export default function StreamSection({
  streamTabs,
  stream,
  onViewStream,
  onOpenMemorialPage,
  onOpenCallsheet,
  activeTab,
  onTabChange,
}: StreamSectionProps) {
  return (
    <Paper sx={{ borderRadius: "12px", p: 2, boxShadow: 0, height: "100%" }}>
      {/* Tabs */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {streamTabs.map((tab, index) => {
          const isActive = index === activeTab;

          return (
            <Button
              key={index}
              onClick={() => onTabChange(index)}
              sx={{
                bgcolor: isActive ? "#724e41" : "transparent",
                color: isActive ? "white" : "#724e41",
                border: isActive ? "none" : "1px solid #724e41",
                borderRadius: "10px",
                px: 1.5,
                py: 0.6,
                minWidth: "auto",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "12px",
                "&:hover": {
                  bgcolor: isActive ? "#5d3e33" : "rgba(114, 78, 65, 0.06)",
                },
              }}
            >
              {tab}
            </Button>
          );
        })}
      </Stack>

      {/* Card container */}
      <Paper
        sx={{
          border: "1px solid #f0f4f8",
          borderRadius: "14px",
          p: 2,
        }}
        elevation={0}
      >
        {/* LEFT + RIGHT layout */}
        <Stack direction="row" spacing={2} alignItems="stretch">
          {/* LEFT: Stream details */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#f0f4f8",
              borderRadius: "12px",
              p: 2,
              position: "relative",
              minHeight: 150,
            }}
          >
            <Chip
              label={`ID:${stream.id}`}
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                bgcolor: "white",
                height: 22,
                "& .MuiChip-label": {
                  fontSize: "10px",
                  color: "#64748b",
                  px: 1,
                  fontWeight: 600,
                },
              }}
            />

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "20px",
                color: "#4b5563",
                mt: 4,
                mb: 2,
                textTransform: "uppercase",
              }}
            >
              {stream.title}
            </Typography>

            <Divider sx={{ mb: 2, opacity: 0.6 }} />

            <Stack direction="row" spacing={4}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <Box>
                  <Typography sx={{ fontSize: 11, color: "#64748b", ml: 3.5 }}>
                    DATE & TIME
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CalendarIcon
                      sx={{ fontSize: 18, color: "#64748b", mt: "2px" }}
                    />
                    <Typography
                      sx={{ fontWeight: 800, fontSize: 14, color: "#475569" }}
                    >
                      {stream.date}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: 12, color: "#64748b", ml: 3.5 }}>
                    {stream.time}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <Box>
                  <Typography sx={{ fontSize: 11, color: "#64748b", ml: 3.5 }}>
                    Location
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <LocationIcon
                      sx={{ fontSize: 18, color: "#64748b", mt: "2px" }}
                    />
                    <Typography
                      sx={{ fontWeight: 800, fontSize: 14, color: "#475569" }}
                    >
                      {stream.locationName}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: 12, color: "#64748b", ml: 3.5 }}>
                    {stream.locationAddress}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ width: 320 }}>
            <Button
              onClick={onViewStream}
              fullWidth
              sx={{
                bgcolor: "#724e41",
                color: "white",
                borderRadius: "14px",
                p: 2,
                textTransform: "none",
                justifyContent: "space-between",
                alignItems: "center",
                "&:hover": { bgcolor: "#5d3e33" },
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
                  Live stream
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
                  View Stream
                </Typography>
              </Box>

              <PlayCircleFilledRounded sx={{ fontSize: 34 }} />
            </Button>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Button
                onClick={onOpenMemorialPage}
                fullWidth
                sx={{
                  bgcolor: "#f1f5f9",
                  borderRadius: "14px",
                  p: 2,
                  textTransform: "none",
                  justifyContent: "space-between",
                  "&:hover": { bgcolor: "#eaeef5" },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "white",
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <PublicRounded sx={{ color: "#475569" }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: "#475569" }}>
                    Memorial page
                  </Typography>
                </Stack>

                <KeyboardArrowRightRounded sx={{ color: "#94a3b8" }} />
              </Button>

              <Button
                onClick={onOpenCallsheet}
                fullWidth
                sx={{
                  bgcolor: "#f1f5f9",
                  borderRadius: "14px",
                  p: 2,
                  textTransform: "none",
                  justifyContent: "space-between",
                  "&:hover": { bgcolor: "#eaeef5" },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "white",
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <DescriptionRounded sx={{ color: "#475569" }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: "#475569" }}>
                    Callsheet
                  </Typography>
                </Stack>

                <KeyboardArrowRightRounded sx={{ color: "#94a3b8" }} />
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Paper>
  );
}
